import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { gsap } from '../../lib/gsapSetup';
import { cn } from '../../lib/cn';
import { useTranslation } from '../../i18n/LanguageContext';
import logoWhite from '../../assets/logo/Main_white.png';
import logoDark from '../../assets/logo/Main.png';

const NAV_LINK_IDS = ['features', 'how-it-works', 'app', 'reseller', 'faq'];
const NAV_LINK_KEYS = [
  'nav.features',
  'nav.howItWorks',
  'nav.app',
  'nav.reseller',
  'nav.faq',
];

// Sections whose background is dark — use white logo
const DARK_SECTIONS = new Set(['hero', 'download']);

/**
 * NAVBAR — MetaMask-inspired floating pill nav.
 * Transparent → glass pill morph on scroll.
 * Clip-path circular reveal for mobile menu.
 * Language toggle FR/EN.
 */
export default function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');
  const [isDark, setIsDark] = useState(true); // hero is dark on load
  const navRef = useRef(null);
  const menuRef = useRef(null);
  const menuTlRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === '/';
  const { t, lang, setLang } = useTranslation();

  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Observe which section is at the top of the viewport to swap logo
  useEffect(() => {
    const sectionIds = [
      'hero',
      'features',
      'how-it-works',
      'video',
      'app',
      'reseller',
      'download',
      'faq',
    ];
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsDark(DARK_SECTIONS.has(entry.target.id));
          }
        });
      },
      { rootMargin: '-10px 0px -80% 0px' },
    );
    sectionIds.forEach((id) => {
      const el = document.getElementById(id);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!isHome) return;
    const ids = ['hero', ...NAV_LINK_IDS, 'download'];
    const observers = [];
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (!el) return;
      const obs = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) setActiveSection(id);
        },
        { rootMargin: '-45% 0px -50% 0px' },
      );
      obs.observe(el);
      observers.push(obs);
    });
    return () => observers.forEach((o) => o.disconnect());
  }, [isHome]);

  useEffect(() => {
    if (!menuRef.current) return;
    const links = menuRef.current.querySelectorAll('.menu-link');
    const preview = menuRef.current.querySelector('.menu-preview');

    gsap.set(menuRef.current, { autoAlpha: 0, pointerEvents: 'none' });
    gsap.set(links, { y: 30, opacity: 0 });
    gsap.set(preview, { x: 50, opacity: 0, scale: 1.06 });

    menuTlRef.current = gsap
      .timeline({ paused: true })
      .to(menuRef.current, {
        autoAlpha: 1,
        pointerEvents: 'auto',
        duration: 0.35,
        ease: 'power2.out',
      })
      .fromTo(
        menuRef.current,
        { clipPath: 'circle(0% at calc(100% - 52px) 44px)' },
        {
          clipPath: 'circle(150% at calc(100% - 52px) 44px)',
          duration: 0.9,
          ease: 'power4.inOut',
        },
        0,
      )
      .to(
        links,
        {
          y: 0,
          opacity: 1,
          stagger: 0.08,
          duration: 0.55,
          ease: 'power3.out',
        },
        0.2,
      )
      .to(
        preview,
        {
          x: 0,
          opacity: 1,
          scale: 1,
          duration: 0.8,
          ease: 'power3.out',
        },
        0.28,
      );

    return () => {
      menuTlRef.current?.kill();
      document.body.style.overflow = '';
    };
  }, []);

  useEffect(() => {
    if (!menuTlRef.current) return;
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
      menuTlRef.current.play();
    } else {
      document.body.style.overflow = '';
      menuTlRef.current.reverse();
    }
  }, [menuOpen]);

  const scrollTo = useCallback(
    (id) => {
      setMenuOpen(false);
      if (!isHome) {
        sessionStorage.setItem('scrollToSection', id);
        navigate('/');
        return;
      }
      const el = document.getElementById(id);
      if (el) {
        const top = el.getBoundingClientRect().top + window.scrollY - 80;
        window.scrollTo({ top, behavior: 'smooth' });
      }
    },
    [isHome, navigate],
  );

  return (
    <>
      <header ref={navRef} className='fixed top-0 left-0 right-0 z-50 py-5'>
        <div className='section-container'>
          <div className='flex items-center justify-between px-2'>
            <Link to='/' className='flex-shrink-0 relative z-10 group'>
              <img
                src={isDark ? logoWhite : logoDark}
                alt='LinxExpress'
                className='h-16 lg:h-20 w-auto transition-all duration-500 group-hover:scale-105'
                loading='eager'
              />
            </Link>
            <button
              onClick={() => setMenuOpen((prev) => !prev)}
              className='relative z-[60] w-12 h-12 flex items-center justify-center rounded-full border border-primary/[0.18] bg-white/80 backdrop-blur-md shadow-sm'
              aria-label={menuOpen ? t('nav.close') : t('nav.menu')}
              aria-expanded={menuOpen}
            >
              <div className='w-5 h-[14px] relative flex flex-col justify-between'>
                <span
                  className={cn(
                    'block w-full h-[2px] bg-primary rounded-full transition-all duration-500 origin-center',
                    menuOpen && 'rotate-45 translate-y-[6px]',
                  )}
                />
                <span
                  className={cn(
                    'block w-full h-[2px] bg-primary rounded-full transition-all duration-300',
                    menuOpen ? 'opacity-0 scale-x-0' : 'opacity-100',
                  )}
                />
                <span
                  className={cn(
                    'block w-full h-[2px] bg-primary rounded-full transition-all duration-500 origin-center',
                    menuOpen && '-rotate-45 -translate-y-[6px]',
                  )}
                />
              </div>
            </button>
          </div>
        </div>
      </header>

      <div
        ref={menuRef}
        className='fixed inset-0 z-[55] bg-[#0A174E]'
        style={{ clipPath: 'circle(0% at calc(100% - 52px) 44px)' }}
      >
        {/* ── Prominent close button ── */}
        <button
          onClick={() => setMenuOpen(false)}
          aria-label={t('nav.close')}
          className='absolute top-5 right-4 sm:right-6 lg:right-8 z-[70] w-12 h-12 flex items-center justify-center rounded-full bg-white/10 border border-white/25 text-white hover:bg-white/20 transition-colors duration-300'
        >
          <svg
            width='18'
            height='18'
            viewBox='0 0 24 24'
            fill='none'
            stroke='currentColor'
            strokeWidth='2.5'
            strokeLinecap='round'
          >
            <line x1='18' y1='6' x2='6' y2='18' />
            <line x1='6' y1='6' x2='18' y2='18' />
          </svg>
        </button>

        <div className='section-container h-full py-24 lg:py-20'>
          <div className='grid grid-cols-1 lg:grid-cols-2 gap-12 h-full items-center'>
            <nav className='flex flex-col items-start justify-center gap-4 sm:gap-5'>
              {NAV_LINK_IDS.map((id, idx) => (
                <button
                  key={id}
                  onClick={() => scrollTo(id)}
                  className={cn(
                    'menu-link text-left font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight transition-colors duration-300',
                    activeSection === id && isHome
                      ? 'text-white'
                      : 'text-white/55 hover:text-white',
                  )}
                >
                  {t(NAV_LINK_KEYS[idx])}
                </button>
              ))}
              <button
                onClick={() => scrollTo('download')}
                className='menu-link mt-6 inline-flex items-center gap-2 px-7 py-3 rounded-full text-base font-semibold bg-secondary text-primary'
              >
                {t('nav.download')}
              </button>

              {/* FR / AR language toggle */}
              <div className='menu-link mt-8 flex items-center gap-1 p-1 rounded-full border border-white/20 w-fit'>
                {['fr', 'ar'].map((code) => (
                  <button
                    key={code}
                    onClick={() => setLang(code)}
                    className={cn(
                      'px-4 py-1.5 rounded-full text-sm font-semibold transition-all duration-300',
                      lang === code
                        ? 'bg-white text-primary'
                        : 'text-white/60 hover:text-white',
                    )}
                  >
                    {code === 'fr' ? 'FR' : 'AR'}
                  </button>
                ))}
              </div>
            </nav>

            <div className='menu-preview hidden lg:flex items-center justify-center'>
              <img
                src={logoWhite}
                alt='LinxExpress'
                className='w-[min(36vw,480px)] object-contain drop-shadow-[0_0_60px_rgba(255,255,255,0.15)]'
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
